import React, { useState, useRef, useEffect } from 'react';
import { User, LogOut } from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { useTheme } from '../../ThemeContext';
import { AlertBox, FormLabel, FormInput, ModalShell } from '../ui';


export default function WelcomeModal({ 
  show, 
  onClose, 
  walletAddress,
  onAuthorCreated,
  onDisconnect
}) {
  const { t, isDark } = useTheme();
  const [name, setName] = useState('');
  const [bio, setBio] = useState('');
  const [handle, setHandle] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [handleAvailable, setHandleAvailable] = useState(null);
  const [checkingHandle, setCheckingHandle] = useState(false);
  const handleCheckTimer = useRef(null);

  useEffect(() => {
    return () => {
      if (handleCheckTimer.current) clearTimeout(handleCheckTimer.current);
    };
  }, []);

  if (!show) return null;

  const checkHandleAvailability = async (value) => {
    if (!value.trim() || value.length < 3) {
      setHandleAvailable(null);
      return;
    }

    setCheckingHandle(true);
    try {
      const { data, error } = await supabase
        .from('entity_handles')
        .select('handle')
        .eq('handle', value.toLowerCase())
        .maybeSingle();

      if (error) {
        console.error('Handle check error:', error);
        setHandleAvailable(null);
      } else if (data) {
        setHandleAvailable(false);
      } else {
        setHandleAvailable(true);
      }
    } catch (err) {
      console.error('Error checking handle:', err);
      setHandleAvailable(null);
    }
    setCheckingHandle(false);
  };

  const handleHandleChange = (e) => {
    const value = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '');
    setHandle(value);
    
    if (handleCheckTimer.current) clearTimeout(handleCheckTimer.current);
    handleCheckTimer.current = setTimeout(() => {
      checkHandleAvailability(value);
    }, 500);
  };

  const handleDismiss = () => {
    // Remember dismissal for this wallet so it doesn't loop
    localStorage.setItem('limestone_welcome_dismissed', walletAddress);
    onClose();
  };

  const handleDisconnect = () => {
    localStorage.removeItem('limestone_welcome_dismissed');
    if (onDisconnect) {
      onDisconnect();
    }
    onClose();
  };

  const createAuthor = async () => {
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    if (!handle.trim() || handle.length < 3) {
      setError('Handle must be at least 3 characters');
      return;
    }
    if (handleAvailable === false) {
      setError('Handle is already taken');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const authorId = `author_${Date.now()}`;
      const timestamp = Date.now();

      const { data: authorData, error: authorError } = await supabase
        .from('authors')
        .insert({
          id: authorId,
          name: name.trim(),
          bio: bio.trim() || null,
          wallet_address: walletAddress,
          created_at: timestamp
        })
        .select()
        .single();

      if (authorError) {
        console.error('Author creation error:', authorError);
        throw authorError;
      }

      const { data: handleData, error: handleError } = await supabase
        .from('entity_handles')
        .insert({
          handle: handle.toLowerCase(),
          entity_type: 'individual',
          entity_id: authorId,
          created_at: timestamp
        })
        .select()
        .single();

      if (handleError) {
        console.error('Handle creation error:', handleError);
        await supabase.from('authors').delete().eq('id', authorId);
        throw handleError;
      }

      // Clear dismissal flag on successful creation
      localStorage.removeItem('limestone_welcome_dismissed');
      setIsSubmitting(false);
      onAuthorCreated();
    } catch (err) {
      console.error('Error creating author:', err);
      setError(`Failed to create profile: ${err.message || 'Unknown error'}`);
      setIsSubmitting(false);
    }
  };

  return (
    <ModalShell blur panelStyle={{ backgroundColor: t.modalBg, padding: '40px', boxShadow: '0 8px 24px rgba(0,0,0,0.2)' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            backgroundColor: t.buttonBg,
            color: t.buttonText,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
            fontSize: '32px'
          }}>
            <User size={40} />
          </div>
          <h2 style={{
            fontSize: '28px',
            fontWeight: '600',
            color: t.text,
            marginBottom: '8px',
            fontFamily: 'Poppins, sans-serif'
          }}>
            Welcome to Limestone!
          </h2>
          <p style={{
            fontSize: '14px',
            color: t.textSecondary,
            fontFamily: 'Poppins, sans-serif',
            lineHeight: '1.6'
          }}>
            Create your author profile to start writing and publishing.
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <AlertBox>{error}</AlertBox>
        )}

        {/* Form */}
        <div style={{ marginBottom: '16px' }}>
          <FormLabel>Display Name *</FormLabel>
          <FormInput
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name or pen name"
          />
        </div>

        <div style={{ marginBottom: '16px' }}>
          <FormLabel>Handle * <span style={{ fontWeight: '400', color: t.textSecondary }}>(@username)</span></FormLabel>
          <div style={{ position: 'relative' }}>
            <span style={{
              position: 'absolute',
              left: '16px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: t.textSecondary,
              fontSize: '14px'
            }}>@</span>
            <input
              type="text"
              value={handle}
              onChange={handleHandleChange}
              placeholder="yourhandle"
              style={{
                width: '100%',
                padding: '12px 16px 12px 32px',
                borderRadius: '8px',
                border: `1px solid ${handleAvailable === false ? t.error : handleAvailable === true ? (t.success || '#16a34a') : t.borderStrong}`,
                fontSize: '14px',
                fontFamily: 'Poppins, sans-serif',
                backgroundColor: t.surface,
                outline: 'none',
                color: t.text
              }}
            />
          </div>
          {handle.length >= 3 && (
            <p style={{
              fontSize: '12px',
              marginTop: '4px',
              fontFamily: 'Poppins, sans-serif',
              color: checkingHandle ? t.textSecondary : handleAvailable === true ? (t.success || '#16a34a') : handleAvailable === false ? t.error : t.textSecondary
            }}>
              {checkingHandle ? 'Checking...' : handleAvailable === true ? '✓ Available' : handleAvailable === false ? '✗ Already taken' : ''}
            </p>
          )}
        </div>

        <div style={{ marginBottom: '24px' }}>
          <FormLabel>Bio <span style={{ fontWeight: '400', color: t.textSecondary }}>(optional)</span></FormLabel>
          <textarea
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            placeholder="Tell readers about yourself..."
            style={{
              width: '100%',
              padding: '12px 16px',
              borderRadius: '8px',
              border: `1px solid ${t.borderStrong}`,
              fontSize: '14px',
              fontFamily: 'Poppins, sans-serif',
              backgroundColor: t.surface,
              outline: 'none',
              color: t.text,
              minHeight: '80px',
              resize: 'vertical'
            }}
            onFocus={(e) => e.target.style.borderColor = t.accent}
            onBlur={(e) => e.target.style.borderColor = t.borderStrong}
          />
        </div>

        {/* Primary Action */}
        <button
          onClick={createAuthor}
          disabled={!name.trim() || !handle.trim() || handle.length < 3 || handleAvailable === false || checkingHandle || isSubmitting}
          style={{
            width: '100%',
            backgroundColor: t.buttonBg,
            color: t.buttonText,
            padding: '14px',
            borderRadius: '12px',
            border: 'none',
            fontSize: '16px',
            fontWeight: '600',
            fontFamily: 'Poppins, sans-serif',
            cursor: (!name.trim() || !handle.trim() || handle.length < 3 || handleAvailable === false || checkingHandle || isSubmitting) ? 'not-allowed' : 'pointer',
            opacity: (!name.trim() || !handle.trim() || handle.length < 3 || handleAvailable === false || checkingHandle || isSubmitting) ? 0.5 : 1,
            transition: 'all 0.2s',
            marginBottom: '16px'
          }}
        >
          {isSubmitting ? 'Creating...' : 'Create Profile & Get Started'}
        </button>

        {/* Escape hatches */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          paddingTop: '16px',
          borderTop: `1px solid ${t.border || t.borderStrong}`
        }}>
          <button
            onClick={handleDismiss}
            style={{
              background: 'none',
              border: 'none',
              color: t.textSecondary,
              fontSize: '13px',
              fontFamily: 'Poppins, sans-serif',
              cursor: 'pointer',
              padding: '4px 0',
              textDecoration: 'underline'
            }}
          >
            Skip for now
          </button>

          <button
            onClick={handleDisconnect}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              background: 'none',
              border: 'none',
              color: t.textSecondary,
              fontSize: '13px',
              fontFamily: 'Poppins, sans-serif',
              cursor: 'pointer',
              padding: '4px 0'
            }}
          >
            <LogOut size={14} />
            Disconnect wallet
          </button>
        </div>
    </ModalShell>
  );
}